import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  RichTextEditor,
  RichTextViewer,
} from "~/components/ui/rich-text-editor";
import { Textarea } from "~/components/ui/textarea";
import {
  adminArchiveArticleFn,
  adminPublishArticleFn,
  adminRestoreArticleFn,
  adminUpdateArticleFn,
  approveArticleFn,
  articleQueryKeys,
  articleQueryOptions,
  createArticleDraftFn,
  promoteArticleDraftFn,
  returnArticleToDraftFn,
  type AdminArticleRow,
} from "~/lib/functions/cih/articles";

export const Route = createFileRoute("/admin/cih/articles/$articleId")({
  staticData: { title: "Review Article" },
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      articleQueryOptions(params.articleId),
    );
    return { title: data?.title ?? "Review Article" };
  },
  component: ArticleReviewPage,
});

function ArticleReviewPage() {
  const { articleId } = Route.useParams();
  const { data: article, isLoading } = useQuery(articleQueryOptions(articleId));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Article not found.</div>
      </div>
    );
  }

  return <ArticleEditor articleId={articleId} article={article} />;
}

function ArticleEditor({
  articleId,
  article,
}: {
  articleId: string;
  article: AdminArticleRow;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [returnNote, setReturnNote] = useState("");
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiveNote, setArchiveNote] = useState("");

  const isDraft = article.status === "draft";
  const isInReview = article.status === "in_review";
  const isPublished = article.status === "published";
  const isArchived = article.status === "archived";
  const hasDraftParent = article.draftOf != null;
  const isCoachless = article.coachId == null;

  const form = useForm({
    defaultValues: {
      title: article.title,
      description: article.description,
      body: article.body != null ? JSON.stringify(article.body) : "",
      authorName: article.authorName ?? "",
      authorTitle: article.authorTitle ?? "",
    },
  });

  const isDirty = useStore(form.store, (state) => state.isDirty);

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({
      queryKey: articleQueryKeys.detail(articleId),
    });
    queryClient.invalidateQueries({ queryKey: ["articles", "admin"] });
  };

  const getFormPayload = () => {
    const v = form.state.values;
    return {
      id: articleId,
      title: v.title,
      description: v.description,
      body: v.body ? safeParse(v.body) : undefined,
      ...(isCoachless
        ? {
            authorName: v.authorName || undefined,
            authorTitle: v.authorTitle,
          }
        : {}),
    };
  };

  const saveMutation = useMutation({
    mutationFn: () => adminUpdateArticleFn({ data: getFormPayload() }),
    onSuccess: () => {
      invalidate();
      showSuccess("Article saved.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveArticleFn,
    onSuccess: () => {
      invalidate();
      showSuccess(
        hasDraftParent
          ? "Edits approved and promoted to live."
          : "Article approved and published.",
      );
    },
  });

  const returnMutation = useMutation({
    mutationFn: returnArticleToDraftFn,
    onSuccess: () => {
      invalidate();
      setReturnNote("");
      setShowReturnForm(false);
      showSuccess("Article returned to coach for revision.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createArticleDraftFn,
    onSuccess: (draft) => {
      invalidate();
      navigate({
        to: "/admin/cih/articles/$articleId",
        params: { articleId: draft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      if (isDirty) {
        await adminUpdateArticleFn({ data: getFormPayload() });
      }
      return promoteArticleDraftFn(data);
    },
    onSuccess: (promoted) => {
      queryClient.removeQueries({
        queryKey: articleQueryKeys.detail(articleId),
      });
      invalidate();
      navigate({
        to: "/admin/cih/articles/$articleId",
        params: { articleId: promoted.id },
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (isDirty) {
        await adminUpdateArticleFn({ data: getFormPayload() });
      }
      return adminPublishArticleFn({ data: { id: articleId } });
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Article published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: adminArchiveArticleFn,
    onSuccess: (result) => {
      setConfirmArchiveOpen(false);
      setArchiveNote("");
      // Discarded draft-of-published rows no longer exist; bounce to the live
      // article (or the index if we somehow can't resolve it). Skip the usual
      // invalidate() — re-fetching the just-deleted detail row would 404 — and
      // only refresh the admin list so the row drops out of it.
      if (result.discarded) {
        queryClient.removeQueries({
          queryKey: articleQueryKeys.detail(articleId),
        });
        queryClient.invalidateQueries({ queryKey: ["articles", "admin"] });
        if (article.draftOf) {
          navigate({
            to: "/admin/cih/articles/$articleId",
            params: { articleId: article.draftOf },
          });
        } else {
          navigate({ to: "/admin/cih/articles" });
        }
        return;
      }
      invalidate();
      showSuccess("Article archived.");
    },
  });

  // Coach-less editorial articles have no recipient for an archive
  // notification, so we hide the note input for them. Same for the discard-
  // edits flow: the live article is untouched, so there's nothing to explain.
  const canExplainArchive = !hasDraftParent && !isCoachless;

  const restoreMutation = useMutation({
    mutationFn: adminRestoreArticleFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Article restored to draft.");
    },
  });

  const anyPending =
    saveMutation.isPending ||
    approveMutation.isPending ||
    returnMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    restoreMutation.isPending;

  const mutationError =
    saveMutation.error ??
    approveMutation.error ??
    returnMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    restoreMutation.error;

  // Admins edit when this is a draft of a published article (their own working
  // copy), a coach-less editorial draft, or a coach submission currently in
  // review (lets editorial fix typos before approving). Coach drafts belong to
  // the coach and stay read-only.
  const adminCanEdit =
    isInReview || (isDraft && (hasDraftParent || isCoachless));
  // A brand-new coach-less editorial draft can be published directly.
  const canPublish = isDraft && isCoachless && !hasDraftParent;
  const canArchive = !isArchived;
  // Discarding edits of a live article reads differently from archiving an
  // independent row — the live article stays put and only the in-flight draft
  // disappears.
  const archiveActionLabel = hasDraftParent ? "Discard Edits" : "Archive";
  const archiveDialogCopy = hasDraftParent
    ? {
        title: "Discard these edits?",
        description:
          "The draft and its changes will be deleted. The live article stays as it is.",
        confirmLabel: "Discard edits",
      }
    : isPublished
      ? {
          title: "Archive this article?",
          description:
            "The article will be hidden from the public site and the active queue. You can restore it from the Archived tab.",
          confirmLabel: "Archive article",
        }
      : {
          title: "Archive this article?",
          description:
            "The article will be hidden from the active queue. You can restore it from the Archived tab.",
          confirmLabel: "Archive article",
        };
  const bodyString =
    article.body != null ? JSON.stringify(article.body) : undefined;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">
                {article.title}
              </h1>
              <ContentStatusBadge status={article.status} />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              By{" "}
              {article.coachUsername ? (
                <a
                  href={`/${article.coachUsername}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-gray-700 hover:text-brand-600 hover:underline"
                >
                  {article.coachFullName}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-gray-700">
                  {article.authorName}
                  {article.authorTitle ? ` · ${article.authorTitle}` : ""}
                </span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isPublished && (
              <Button
                variant="default"
                onClick={() =>
                  createDraftMutation.mutate({
                    data: { publishedId: articleId },
                  })
                }
                disabled={anyPending}
                loading={createDraftMutation.isPending}
              >
                Edit
              </Button>
            )}
            {adminCanEdit && (
              <Button
                variant="default"
                onClick={() => saveMutation.mutate()}
                disabled={anyPending}
                loading={saveMutation.isPending}
              >
                Save
              </Button>
            )}
            {canPublish && (
              <Button
                variant="primary"
                className="border-green-700 bg-green-600 hover:border-green-600 hover:bg-green-700"
                onClick={() => publishMutation.mutate()}
                disabled={anyPending}
                loading={publishMutation.isPending}
              >
                Publish
              </Button>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  className="border-green-700 bg-green-600 hover:border-green-600 hover:bg-green-700"
                  onClick={async () => {
                    if (isDirty) {
                      await saveMutation.mutateAsync();
                    }
                    approveMutation.mutate({ data: { id: articleId } });
                  }}
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={() => setShowReturnForm((v) => !v)}
                  disabled={anyPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() =>
                  restoreMutation.mutate({ data: { id: articleId } })
                }
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
            {canArchive && (
              <Button
                variant="destructive-ghost"
                onClick={() => setConfirmArchiveOpen(true)}
                disabled={anyPending}
              >
                {archiveActionLabel}
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner (admin editing their own draft of a live article) */}
        {adminCanEdit && isDraft && hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published article. Save your changes, then
              promote when ready.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                promoteMutation.mutate({ data: { draftId: articleId } })
              }
              disabled={anyPending}
              loading={promoteMutation.isPending}
              className="border-amber-700! from-amber-600! to-amber-700! hover:from-amber-500! hover:to-amber-600!"
            >
              Promote to Live
            </Button>
          </div>
        )}

        {isInReview && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {hasDraftParent
              ? "This is an edit to a live article. Make any minor tweaks, then approve to promote these changes onto the published version."
              : "This article is awaiting review. Make any minor edits inline, then approve to publish."}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Return-to-draft form */}
        {showReturnForm && isInReview && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <Label className="mb-2 text-amber-800">
              Feedback for the coach
            </Label>
            <Textarea
              rows={4}
              value={returnNote}
              onValueChange={setReturnNote}
              placeholder="Explain what needs to be revised..."
            />
            <div className="mt-3 flex gap-2">
              <Button
                variant="primary"
                size="sm"
                className="border-amber-700 bg-amber-600 hover:border-amber-600 hover:bg-amber-700"
                onClick={() =>
                  returnMutation.mutate({
                    data: { id: articleId, note: returnNote.trim() },
                  })
                }
                disabled={!returnNote.trim() || anyPending}
                loading={returnMutation.isPending}
              >
                Send Feedback
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setShowReturnForm(false);
                  setReturnNote("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Previous review note (for drafts sent back) */}
        {article.reviewNote && article.status === "draft" && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="mb-1 text-xs font-semibold tracking-wide text-amber-700 uppercase">
              Previous Feedback
            </p>
            <p className="text-sm text-amber-800">{article.reviewNote}</p>
          </div>
        )}

        {/* Metadata */}
        <Card className="mb-6 grid grid-cols-2 sm:grid-cols-3">
          <div>
            <p className="text-xs text-gray-500">Status</p>
            <ContentStatusBadge status={article.status} />
          </div>
          <div>
            <p className="text-xs text-gray-500">Author</p>
            {article.coachUsername ? (
              <a
                href={`/${article.coachUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-brand-600 hover:underline"
              >
                {article.coachFullName}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-sm text-gray-700">
                {article.authorName}
                {article.authorTitle ? ` · ${article.authorTitle}` : ""}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500">Challenge</p>
            {article.challengeSlug ? (
              <a
                href={`/coaching-for/${article.challengeSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-brand-600 hover:underline"
              >
                {article.challengeTitle}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-sm text-gray-700">{article.challengeTitle}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500">Slug</p>
            <p className="font-mono text-sm text-gray-700">{article.slug}</p>
            <p className="mt-1 text-xs text-gray-400">
              Slug must be unique across all articles (not just this challenge).
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Submitted</p>
            <p className="text-sm text-gray-700">
              {article.submittedAt
                ? new Date(article.submittedAt).toLocaleString()
                : "—"}
            </p>
          </div>
          {article.publishedAt && (
            <div>
              <p className="text-xs text-gray-500">Published</p>
              <p className="text-sm text-gray-700">
                {new Date(article.publishedAt).toLocaleString()}
              </p>
              <a
                href={`/articles/${article.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
              >
                View article
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </Card>

        {/* Edit form (admin draft-of-published) or read-only viewer */}
        {adminCanEdit ? (
          <Card className="space-y-4">
            {isCoachless && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="ad-art-author-name" className="mb-1">
                    Author name
                  </Label>
                  <form.Field name="authorName">
                    {(field) => (
                      <Input
                        id="ad-art-author-name"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g. Dream Coach Match Editorial"
                      />
                    )}
                  </form.Field>
                </div>
                <div>
                  <Label htmlFor="ad-art-author-title" className="mb-1">
                    Author title (optional)
                  </Label>
                  <form.Field name="authorTitle">
                    {(field) => (
                      <Input
                        id="ad-art-author-title"
                        type="text"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                        placeholder="e.g. Editorial Team"
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="ad-art-title" className="mb-1">
                Title
              </Label>
              <form.Field name="title">
                {(field) => (
                  <Input
                    id="ad-art-title"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
            </div>
            <div>
              <Label htmlFor="ad-art-description" className="mb-1">
                Description
              </Label>
              <form.Field name="description">
                {(field) => (
                  <Textarea
                    id="ad-art-description"
                    rows={3}
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
            </div>
            <div>
              <Label className="mb-1">Body</Label>
              <form.Field name="body">
                {(field) => (
                  <RichTextEditor
                    content={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>
            </div>
          </Card>
        ) : (
          <>
            <Card className="mb-6">
              <h2 className="text-sm font-medium text-gray-700">Description</h2>
              <p className="text-sm text-gray-600">{article.description}</p>
            </Card>
            <Card>
              <h2 className="text-sm font-medium text-gray-700">Content</h2>
              {bodyString ? (
                <RichTextViewer content={bodyString} />
              ) : (
                <p className="text-sm text-gray-400">No content yet.</p>
              )}
            </Card>
          </>
        )}

        <Dialog
          open={confirmArchiveOpen}
          onOpenChange={(next) => {
            setConfirmArchiveOpen(next);
            if (!next) setArchiveNote("");
          }}
        >
          <DialogContent showCloseButton={false} className="max-w-md">
            <DialogHeader>
              <DialogTitle>{archiveDialogCopy.title}</DialogTitle>
              <DialogDescription>
                {archiveDialogCopy.description}
              </DialogDescription>
            </DialogHeader>
            {canExplainArchive && (
              <div>
                <Label htmlFor="ad-art-archive-note" className="mb-1">
                  Note for the coach (optional)
                </Label>
                <Textarea
                  id="ad-art-archive-note"
                  rows={3}
                  value={archiveNote}
                  onValueChange={setArchiveNote}
                  placeholder="Why is this being archived? They'll see this in their notification."
                />
              </div>
            )}
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirmArchiveOpen(false)}
                disabled={archiveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  archiveMutation.mutate({
                    data: {
                      id: articleId,
                      note:
                        canExplainArchive && archiveNote.trim()
                          ? archiveNote.trim()
                          : undefined,
                    },
                  })
                }
                disabled={archiveMutation.isPending}
                loading={archiveMutation.isPending}
              >
                {archiveDialogCopy.confirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}
